import type { PresignPhotoResponse } from '@etnrlz/shared';
import { api } from './api';

/**
 * Lê um arquivo como base64 (sem o prefixo `data:...;base64,`) + mediaType,
 * para enviar a um endpoint de IA (ex.: leitura de invoice).
 */
export function fileToBase64(file: File): Promise<{ data: string; mediaType: string }> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(reader.error ?? new Error('Falha ao ler o arquivo'));
    reader.onload = () => {
      const res = String(reader.result);
      const comma = res.indexOf(',');
      resolve({
        data: comma >= 0 ? res.slice(comma + 1) : res,
        mediaType: file.type || 'application/octet-stream',
      });
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Upload genérico de arquivo (foto): presign → PUT direto no S3 → devolve a URL
 * pública. Usado para fotos de veículos (capa e galeria).
 */
export async function uploadFile(file: File | Blob, prefix = 'vehicles'): Promise<string> {
  const contentType = (file as File).type || 'image/jpeg';
  const presign = await api.post<PresignPhotoResponse>('/uploads/presign', { prefix, contentType });
  await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': contentType },
    body: file,
  });
  return presign.publicUrl;
}

/**
 * Fluxo de upload de foto de OS:
 * 1) pede presigned PUT URL ao backend, 2) sobe o binário direto ao S3,
 * 3) associa a chave/URL pública à OS. A Lambda nunca trafega o binário.
 */
export async function uploadOrderPhoto(
  orderId: string,
  file: File | Blob,
  legenda?: string,
): Promise<string> {
  const contentType = (file as File).type || 'image/jpeg';
  const presign = await api.post<PresignPhotoResponse>(`/service-orders/${orderId}/photos`, {
    contentType,
  });
  await fetch(presign.uploadUrl, {
    method: 'PUT',
    headers: { 'content-type': contentType },
    body: file,
  });
  await api.post(`/service-orders/${orderId}/photos`, { key: presign.publicUrl, legenda });
  return presign.publicUrl;
}
